import React from "react";
import { Link } from "react-router-dom";
import { currentRef } from "../lib/affiliateRef";
import { REF_QUERY_PARAM } from "../affiliate/contract";
import { currentPromotionRef } from "../lib/promotionRef";
import { PROMOTION_QUERY_PARAM } from "../reviews/contract";

// A marketplace card opens the practitioner's REAL site. The backend enriches every
// item with `_site_url` / `site_url` (verified custom domain → subdomain origin) —
// knowledge the client cannot derive from `slug` alone (renames, custom domains). An
// absolute URL is a cross-origin navigation, so it renders as a plain <a>, not a
// router <Link>. The destination PATH is written once, here: an item without a site
// URL (older cached payload, degraded response) falls back to the internal
// /t/{slug}{path} route rather than a dead link.
//
// THE REFERRAL HAS TO CROSS THE ORIGIN (contract §2). The marketplace lives on the
// platform host and a practitioner's site is a DIFFERENT ORIGIN, so the `{ticket, expiresAt}`
// this browser stored here is unreadable over there — `localStorage` is per-origin. The
// TICKET (`currentRef()`, the server-signed referral, never the bare code) therefore
// travels in the URL, and `<AffiliateRefCapture />` on the destination re-presents it to
// the server and stores the answer under that origin, without counting a second click.
// The internal `/t/{slug}` fallback is the SAME origin and needs nothing: the value is already in storage.
//
// LIFTED OUT OF `MarketplaceLearn.jsx` when the practitioner PROFILE page (`/marketplace/:slug`)
// gained its own "Book" buttons: two pages that both send a visitor across the origin with
// the referral attached must not each own a copy of the rule, or one of them drifts and the
// affiliate silently stops being paid on half the platform's outbound links.
export const withRef = (href) => {
  const ticket = currentRef();
  if (!ticket) return href;
  return `${href}${href.includes("?") ? "&" : "?"}${REF_QUERY_PARAM}=${encodeURIComponent(ticket)}`;
};

// --- crm-module BEGIN C3.04 (marketplace attribution — write side) ---
// SAME REASONING AS `withRef` ABOVE, one paragraph up: a promotion id captured on
// THIS origin (the platform-hosted featured strip / practitioner list) is
// unreadable `localStorage` on the practice's own origin, so it travels in the URL
// and is re-captured there — see `promotionRef.js`'s own module docstring for why
// this needs no server-fetched policy the way `withRef`'s window does.
export const withPromotion = (href) => {
  const id = currentPromotionRef();
  if (!id || (href && href.includes(`${PROMOTION_QUERY_PARAM}=`))) return href;
  return `${href}${href.includes("?") ? "&" : "?"}${PROMOTION_QUERY_PARAM}=${encodeURIComponent(id)}`;
};

// --- crm-module END ---

export const SiteLink = ({ siteUrl, tenantSlug, path = "", children, ...props }) =>
  siteUrl
    ? <a href={withPromotion(withRef(`${siteUrl}${path}`))} {...props}>{children}</a>
    : <Link to={tenantSlug ? `/t/${tenantSlug}${path}` : path || "/"} {...props}>{children}</Link>;

export default SiteLink;
